import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HbrButton, HbrDrawer, HbrInput, HbrSpinner } from '@harbor/elements-react-standalone';
import DocsIndex from './DocsIndex';
import DocsContent from './DocsContent';
import { findDocForPath } from './docsRouteMap';
import { DocEntry, useVisibleDocs } from './useDocsManifest';
import './DocsDrawer.scss';

const DRAWER_ID = 'docs-drawer';

interface DocsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Contextual help drawer.
 *
 * Opening it shows the documentation for the page you are already on, resolved from the
 * manifest, with the full index one click away. Nothing is added to any page to make
 * this work — the current route is the context.
 */
const DocsDrawer: React.FC<DocsDrawerProps> = ({ open, onClose }) => {
  const location = useLocation();
  const { trees, allEntries, isLoading, error } = useVisibleDocs(open);

  const [selected, setSelected] = useState<DocEntry | undefined>();
  const [anchor, setAnchor] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [browsing, setBrowsing] = useState(false);

  const contextualDoc = useMemo(
    () => findDocForPath(allEntries, location.pathname),
    [allEntries, location.pathname]
  );

  // Each time the drawer opens, start from the current page rather than wherever the
  // reader happened to leave off.
  useEffect(() => {
    if (!open) return;
    setSelected(undefined);
    setAnchor(undefined);
    setSearch('');
    setBrowsing(false);
  }, [open]);

  const active = selected || (browsing ? undefined : contextualDoc);

  const handleSelect = (entry: DocEntry, headingAnchor?: string) => {
    setSelected(entry);
    setAnchor(headingAnchor);
    setBrowsing(false);
  };

  /**
   * Resolve a relative markdown link from one document to another, e.g. a link to
   * `validate-pov.md` inside the opportunity record. Falls back to leaving the reader
   * where they are rather than blanking the drawer on an unknown target.
   */
  const handleNavigate = (markdownFile: string) => {
    const [file, headingAnchor] = markdownFile.split('#');
    const name = file.split('/').pop();
    const target = allEntries.find((candidate) => candidate.path.split('/').pop() === name);
    if (!target) return;
    setSelected(target);
    setAnchor(headingAnchor || undefined);
    setBrowsing(false);
  };

  const handleBrowseAll = () => {
    setSelected(undefined);
    setAnchor(undefined);
    setBrowsing(true);
  };

  const showIndex = !active;

  return (
    <HbrDrawer
      id={DRAWER_ID}
      size="medium"
      open={open}
      overlay
      scrollAware
      onHbr-hide={(e: any) => {
        if ((e.target as HTMLElement)?.id === DRAWER_ID) onClose();
      }}
    >
      <div slot="label">{active ? active.title : 'Documentation'}</div>

      <div className="docs-drawer">
        {isLoading && (
          <div className="flex justify-center py-10">
            <HbrSpinner />
          </div>
        )}

        {!isLoading && error && (
          <div className="my-4 p-3 rounded border border-gray-300 text-sm">
            <p className="font-semibold mb-1">Documentation is unavailable.</p>
            <p className="text-gray-600 m-0">
              {(error as Error)?.message ||
                'The documentation bundle has not been deployed with this build.'}
            </p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {showIndex ? (
              <>
                <HbrInput
                  className="mb-4"
                  placeholder="Search documentation"
                  value={search}
                  clearable
                  onHbr-input={(e: any) => setSearch(e.target?.value ?? '')}
                  onChange={(e: any) => setSearch(e.target?.value ?? '')}
                />

                <DocsIndex
                  trees={trees}
                  search={search}
                  currentDocId={contextualDoc?.id}
                  onSelect={handleSelect}
                />
              </>
            ) : (
              <>
                <div className="docs-drawer__crumb">
                  <HbrButton variant="text" size="small" onClick={handleBrowseAll}>
                    All documentation
                  </HbrButton>
                  {active === contextualDoc && (
                    <span className="docs-drawer__badge">This page</span>
                  )}
                </div>

                <DocsContent
                  entry={active}
                  anchor={anchor}
                  onNavigate={handleNavigate}
                />
              </>
            )}
          </>
        )}
      </div>

      <HbrButton slot="footer" variant="text" onClick={onClose}>
        Close
      </HbrButton>
    </HbrDrawer>
  );
};

export default DocsDrawer;
